import { forwardRef, memo } from 'react';
import { cva, VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/styles';

const menuItemDefaultTextClasses = 'text-text-primary fill-icon-primary';

export const menuItemActiveStateClasses =
  'data-[active=true]:bg-bg-inter-tertiary-active data-[active=true]:hover:bg-bg-inter-tertiary-active data-[active=true]:dark:bg-white/40!';

const menuItemVariants = cva('rounded-sm w-full min-h-9 font-body-md font-semibold transition-all outline-none', {
  variants: {
    surface: {
      button: 'flex items-center gap-2 px-2.5 py-2 cursor-pointer disabled:cursor-not-allowed',
      row: 'relative min-w-0',
    },
    color: {
      default: '',
      destructive: '',
    },
  },
  compoundVariants: [
    {
      surface: 'button',
      color: 'default',
      class: cn(
        menuItemDefaultTextClasses,
        'enabled:hover:bg-bg-sur-secondary enabled:active-self:bg-bg-inter-secondary-hover enabled:focus-visible:bg-bg-inter-secondary-hover',
        'disabled:bg-bg-inter-tertiary-default disabled:text-text-disabled disabled:fill-icon-disabled',
        menuItemActiveStateClasses,
      ),
    },
    {
      surface: 'row',
      color: 'default',
      class: cn(
        menuItemDefaultTextClasses,
        'hover:bg-bg-sur-secondary active:bg-bg-inter-secondary-hover',
        menuItemActiveStateClasses,
      ),
    },
    {
      surface: 'button',
      color: 'destructive',
      class:
        'text-text-destructive fill-icon-error hover:bg-bg-inter-destructive-hover focus-visible:bg-bg-inter-destructive-pressed disabled:bg-bg-inter-tertiary-default disabled:text-text-disabled disabled:fill-icon-disabled',
    },
  ],
  defaultVariants: {
    surface: 'button',
    color: 'default',
  },
});

/** Row surface for menu-like lists where the row is not a single interactive `button` (e.g. primary + secondary actions). */
export const menuItemInteractiveRowVariants = menuItemVariants({ surface: 'row', color: 'default' });

export interface MenuItemBaseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  active?: boolean;
}

interface MenuItemDescriptionProps {
  title: React.ReactNode;
  description: React.ReactNode;
  className?: string;
  /** Merged onto the description row (e.g. `items-center gap-1` for a trailing control). */
  descriptionClassName?: string;
}

export const MenuItemDescription = ({
  title,
  description,
  className,
  descriptionClassName,
}: MenuItemDescriptionProps) => {
  return (
    <div className={cn('flex w-full min-w-0 flex-col items-start', className)}>
      <span className="w-full min-w-0 text-left">{title}</span>

      <span
        className={cn(
          'flex w-full min-w-0 font-body-sm font-normal text-left text-text-tertiary dark:text-white/60',
          descriptionClassName,
        )}
      >
        {description}
      </span>
    </div>
  );
};

export type MenuItemProps = MenuItemBaseProps & Omit<VariantProps<typeof menuItemVariants>, 'surface'>;

const MenuItemBase = (
  { children, className, color = 'default', active, ...rest }: MenuItemProps,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  return (
    <button
      ref={ref}
      type="button"
      data-active={active}
      className={cn(menuItemVariants({ color }), className)}
      {...rest}
    >
      {children}
    </button>
  );
};

export const MenuItem = memo(forwardRef(MenuItemBase));
